import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Package, DollarSign, 
  Pill, Syringe, Shield, Box, ChevronDown, ChevronUp,
  RefreshCw, Search, TrendingUp, Snowflake,
  Edit2, Save, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

// Iconos por categoría
const categoriaIcons = {
  'analgesico_opioide': Syringe,
  'antidiabetico': Pill,
  'inmunizacion': Shield,
  'antibiotico': Pill,
  'benzodiacepina': Pill,
  'gastrointestinal': Pill,
  'analgesico': Pill,
  'cefalosporina': Syringe,
  'betalactamico': Pill,
  'IBP': Pill,
  'AINE': Pill,
  'otros': Package
};

// Colores simplificados por categoría
const categoriaColors = {
  'analgesico_opioide': 'bg-red-50 text-red-700',
  'antidiabetico': 'bg-blue-50 text-blue-700',
  'inmunizacion': 'bg-green-50 text-green-700',
  'antibiotico': 'bg-yellow-50 text-yellow-700',
  'benzodiacepina': 'bg-purple-50 text-purple-700',
  'gastrointestinal': 'bg-orange-50 text-orange-700',
  'analgesico': 'bg-pink-50 text-pink-700',
  'cefalosporina': 'bg-teal-50 text-teal-700',
  'otros': 'bg-gray-50 text-gray-700'
};

const FarmaciaDashboard = ({ token, userInfo, bodegaCodigo, activeFilter }) => {
  const [inventario, setInventario] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('todas');
  const [expandedItems, setExpandedItems] = useState({});
  const [selectedBodega, setSelectedBodega] = useState(bodegaCodigo || userInfo?.bodega || 'CF');
  
  // Estados para modales
  const [editingItem, setEditingItem] = useState(null);
  const [editPrice, setEditPrice] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [savingPrice, setSavingPrice] = useState(false);
  
  // Estado para modal de detalle
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailModalData, setDetailModalData] = useState({ title: '', items: [], type: '' });

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [selectedBodega, token]);

  // Efecto para abrir modal cuando cambia el filtro desde el sidebar
  useEffect(() => {
    if (activeFilter && inventario?.items) {
      const metrics = getMetrics();
      
      switch(activeFilter) {
        case 'all':
          openDetailModal('all', 'Todos los Items', inventario?.items || []);
          break;
        case 'controlados':
          openDetailModal('controlados', 'Medicamentos Controlados', metrics.controlados?.items || []);
          break;
        case 'refrigerados':
          openDetailModal('refrigerados', 'Productos Refrigerados', metrics.refrigerados?.items || []);
          break;
        case 'vigentes':
          openDetailModal('vigentes', 'Productos Vigentes', metrics.vigentes?.items || []);
          break;
        case 'ambiente':
          openDetailModal('ambiente', 'Temperatura Ambiente', metrics.noRefrigerados?.items || []);
          break;
        case 'top3':
          openDetailModal('top3', 'Top 3 Más Valiosos', metrics.top3Valiosos || []);
          break;
        default:
          break;
      }
    }
  }, [activeFilter, inventario]);

  const fetchData = async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const [invResponse, resumenResponse] = await Promise.all([
        axios.get(`${API}/api/farmacia/inventario/${selectedBodega}`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000
        }),
        axios.get(`${API}/api/farmacia/resumen`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000
        })
      ]);
      
      setInventario(invResponse.data);
      setResumen(resumenResponse.data);
    } catch (error) {
      console.error('Error cargando datos de farmacia:', error.message);
      toast.error('Error cargando inventario');
    } finally {
      setLoading(false);
    }
  };

  const getMetrics = () => {
    if (!inventario?.items) return {};
    
    const items = inventario.items;
    const controlados = items.filter(i => i.especificaciones?.controlado);
    const refrigerados = items.filter(i => i.especificaciones?.requiere_refrigeracion);
    const noRefrigerados = items.filter(i => !i.especificaciones?.requiere_refrigeracion);
    const vigentes = items.filter(i => i.estado_vencimiento === 'ok');
    const top3Valiosos = [...items].sort((a, b) => (b.valor?.valor_total || 0) - (a.valor?.valor_total || 0)).slice(0, 3);
    
    return {
      controlados: { count: controlados.length, items: controlados },
      refrigerados: { count: refrigerados.length, items: refrigerados },
      noRefrigerados: { count: noRefrigerados.length, items: noRefrigerados },
      vigentes: { count: vigentes.length, items: vigentes },
      top3Valiosos,
      totalLotes: items.length
    };
  };

  const getFilteredItems = () => {
    if (!inventario?.items) return [];
    
    return inventario.items.filter(item => {
      const matchSearch = searchTerm === '' || 
        item.producto.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.producto.sku.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchCategoria = filtroCategoria === 'todas' || 
        item.especificaciones.categoria === filtroCategoria;
      
      return matchSearch && matchCategoria;
    });
  };

  const toggleExpand = (id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      minimumFractionDigits: 0
    }).format(value);
  };

  const openDetailModal = (type, title, items) => {
    setDetailModalData({ type, title, items });
    setShowDetailModal(true);
  };

  const handleEditPrice = (item) => {
    setEditingItem(item);
    setEditPrice(item.valor?.precio_unitario?.toString() || '0');
    setShowEditModal(true);
  };

  const handleSavePrice = async () => {
    if (!editingItem) return;
    
    setSavingPrice(true);
    try {
      const newPrice = parseFloat(editPrice);
      if (isNaN(newPrice) || newPrice < 0) {
        toast.error('Precio inválido');
        return;
      }
      
      await axios.put(`${API}/api/farmacia/producto/${editingItem.producto.id}/precio`, {
        precio_unitario: newPrice
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Precio actualizado');
      setShowEditModal(false);
      setEditingItem(null);
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar');
    } finally {
      setSavingPrice(false);
    }
  };

  // Item simplificado para el modal
  const renderModalItem = (item) => (
    <div key={item.id} className="p-4 bg-white border rounded-lg hover:shadow-sm transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-medium text-gray-900">{item.producto.nombre}</p>
          <p className="text-sm text-gray-500 mt-1">
            {item.conversion?.cajas} cajas → {item.conversion?.total_unidades} uds
          </p>
        </div>
        <div className="text-right">
          <p className="font-semibold text-emerald-600">{formatCurrency(item.valor?.valor_total || 0)}</p>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => handleEditPrice(item)}
            className="text-blue-600 mt-1"
          >
            <Edit2 className="w-4 h-4 mr-1" />
            Editar
          </Button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600">Cargando...</span>
      </div>
    );
  }

  const filteredItems = getFilteredItems();
  const categorias = inventario?.categorias || {};
  const metrics = getMetrics();

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="farmacia-dashboard">
      {/* Header Simple */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Inventario Farmacia</h1>
          <p className="text-sm text-gray-500">{inventario?.bodega?.nombre}</p>
        </div>
        
        <div className="flex items-center gap-2">
          {resumen?.bodegas?.length > 1 && (
            <select 
              value={selectedBodega}
              onChange={(e) => setSelectedBodega(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm bg-white"
            >
              {resumen.bodegas.map(b => (
                <option key={b.codigo} value={b.codigo}>{b.nombre}</option>
              ))}
            </select>
          )}
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Métricas - Diseño Simple */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border rounded-xl p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Valor Total</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{inventario?.resumen?.valor_formateado || '₡0'}</p>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Unidades</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{inventario?.resumen?.total_unidades?.toLocaleString() || 0}</p>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Controlados</p>
          <p className="text-2xl font-bold text-red-600 mt-1">{metrics.controlados?.count || 0}</p>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Refrigerados</p>
          <p className="text-2xl font-bold text-cyan-600 mt-1">{metrics.refrigerados?.count || 0}</p>
        </div>
      </div>

      {/* Búsqueda Simple */}
      <div className="flex gap-3 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input 
            placeholder="Buscar producto..." 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
            className="pl-10 bg-white"
          />
        </div>
        <select 
          value={filtroCategoria} 
          onChange={(e) => setFiltroCategoria(e.target.value)} 
          className="border rounded-lg px-3 py-2 bg-white text-sm min-w-[150px]"
        >
          <option value="todas">Todas las categorías</option>
          {Object.keys(categorias).map(cat => (
            <option key={cat} value={cat}>{cat.replace(/_/g, ' ')}</option>
          ))}
        </select>
      </div>

      {/* Lista de Items - Diseño Limpio */}
      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50">
          <p className="text-sm font-medium text-gray-700">{filteredItems.length} productos</p>
        </div>
        
        <div className="divide-y">
          {filteredItems.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Package className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p>No se encontraron productos</p>
            </div>
          ) : (
            filteredItems.map((item) => {
              const isExpanded = expandedItems[item.id];
              const Icon = categoriaIcons[item.especificaciones?.categoria] || Package;
              
              return (
                <div key={item.id} className="hover:bg-gray-50 transition-colors">
                  {/* Fila Principal - Solo lo esencial */}
                  <div 
                    className="px-4 py-4 flex items-center justify-between cursor-pointer"
                    onClick={() => toggleExpand(item.id)}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`p-2 rounded-lg ${categoriaColors[item.especificaciones?.categoria] || categoriaColors.otros}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">{item.producto.nombre}</p>
                        <p className="text-sm text-gray-500">
                          {item.conversion?.cajas} cajas = {item.conversion?.total_unidades} uds
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 ml-4">
                      <p className="font-semibold text-emerald-600 text-right">
                        {formatCurrency(item.valor?.valor_total)}
                      </p>
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  </div>
                  
                  {/* Detalles Expandidos */}
                  {isExpanded && (
                    <div className="px-4 pb-4 bg-gray-50">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">SKU</p>
                          <p className="font-medium">{item.producto.sku}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Lote</p>
                          <p className="font-medium">{item.lote}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Precio Unitario</p>
                          <p className="font-medium text-emerald-600">{formatCurrency(item.valor?.precio_unitario)}</p>
                        </div>
                      </div>
                      
                      {/* Sección de Cobertura y Consumo */}
                      <div className="mt-4 p-3 bg-white rounded-lg border">
                        <p className="text-xs font-semibold text-gray-600 uppercase mb-3">📊 Análisis de Cobertura</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">Stock Actual</p>
                            <p className="font-bold text-lg text-blue-600">{item.conversion?.total_unidades} uds</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Consumo/Mes</p>
                            <p className="font-bold text-lg text-purple-600">
                              {item.cobertura?.consumo_promedio_mensual > 0 
                                ? `${item.cobertura.consumo_promedio_mensual} uds`
                                : 'Sin datos'}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">Cobertura</p>
                            <p className={`font-bold text-lg ${
                              item.cobertura?.meses_cobertura === null ? 'text-gray-400' :
                              item.cobertura?.meses_cobertura < 1 ? 'text-red-600' : 
                              item.cobertura?.meses_cobertura < 2 ? 'text-amber-600' : 
                              'text-green-600'
                            }`}>
                              {item.cobertura?.meses_cobertura !== null 
                                ? `${item.cobertura.meses_cobertura} meses`
                                : 'N/A'}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">Vencimiento</p>
                            <p className={`font-bold text-lg ${
                              item.estado_vencimiento === 'vencido' ? 'text-red-600' : 
                              item.estado_vencimiento === 'proximo_vencer' ? 'text-amber-600' : 
                              'text-green-600'
                            }`}>
                              {item.dias_para_vencer !== null 
                                ? item.dias_para_vencer < 0 
                                  ? `Vencido`
                                  : `${item.dias_para_vencer}d`
                                : 'N/A'}
                            </p>
                          </div>
                        </div>
                        
                        {/* Fórmula explicativa */}
                        {item.cobertura?.consumo_promedio_mensual > 0 && (
                          <div className="mt-3 pt-3 border-t text-xs text-gray-500">
                            <span className="font-medium">Fórmula:</span> {item.conversion?.total_unidades} uds ÷ {item.cobertura?.consumo_promedio_mensual} uds/mes = <span className="font-bold text-gray-700">{item.cobertura?.meses_cobertura} meses</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Badges y Acciones */}
                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                        <div className="flex gap-2 flex-wrap">
                          {item.especificaciones?.controlado && (
                            <Badge variant="destructive" className="text-xs">Controlado</Badge>
                          )}
                          {item.especificaciones?.requiere_refrigeracion && (
                            <Badge className="text-xs bg-cyan-100 text-cyan-700">Refrigerado</Badge>
                          )}
                          {item.cobertura?.alerta_cobertura && (
                            <Badge className="text-xs bg-orange-100 text-orange-700">⚠️ Baja cobertura</Badge>
                          )}
                          <Badge variant="outline" className="text-xs capitalize">
                            {item.especificaciones?.categoria?.replace(/_/g, ' ') || 'General'}
                          </Badge>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); handleEditPrice(item); }}
                        >
                          <Edit2 className="w-3 h-3 mr-1" />
                          Editar Precio
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Resumen por Bodega */}
      {resumen?.bodegas?.length > 1 && (
        <div className="mt-6 bg-white border rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50">
            <p className="text-sm font-medium text-gray-700">Resumen General</p>
          </div>
          <div className="divide-y">
            {resumen.bodegas.map(b => (
              <div key={b.codigo} className="px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {b.codigo === 'CF' ? (
                    <Snowflake className="w-4 h-4 text-cyan-500" />
                  ) : (
                    <Box className="w-4 h-4 text-gray-400" />
                  )}
                  <span className="text-gray-700">{b.nombre}</span>
                </div>
                <div className="flex items-center gap-6 text-sm">
                  <span className="text-gray-500">{b.items} items</span>
                  <span className="font-semibold text-emerald-600">{b.valor_formateado}</span>
                </div>
              </div>
            ))}
            <div className="px-4 py-3 bg-emerald-50 flex items-center justify-between">
              <span className="font-medium text-emerald-800">Total</span>
              <span className="font-bold text-emerald-700">{resumen.gran_total_formateado}</span>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalle */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-lg max-h-[70vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{detailModalData.title}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3">
            {detailModalData.items.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Sin items</p>
            ) : (
              <>
                <div className="bg-gray-100 p-3 rounded-lg flex justify-between text-sm">
                  <span>{detailModalData.items.length} items</span>
                  <span className="font-semibold">
                    {formatCurrency(detailModalData.items.reduce((sum, i) => sum + (i.valor?.valor_total || 0), 0))}
                  </span>
                </div>
                {detailModalData.items.map(item => renderModalItem(item))}
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Precio */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Editar Precio</DialogTitle>
          </DialogHeader>
          
          {editingItem && (
            <div className="space-y-4">
              <div>
                <p className="font-medium">{editingItem.producto.nombre}</p>
                <p className="text-sm text-gray-500">{editingItem.producto.sku}</p>
              </div>
              
              <div>
                <label className="text-sm text-gray-600">Precio Unitario</label>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-gray-400">₡</span>
                  <Input 
                    type="number" 
                    value={editPrice} 
                    onChange={(e) => setEditPrice(e.target.value)} 
                    className="flex-1"
                  />
                </div>
              </div>
              
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm text-blue-600">Nuevo valor del lote</p>
                <p className="text-xl font-bold text-blue-700">
                  {formatCurrency((parseFloat(editPrice) || 0) * (editingItem.conversion?.total_unidades || 0))}
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancelar</Button>
            <Button onClick={handleSavePrice} disabled={savingPrice}>
              {savingPrice ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FarmaciaDashboard;
